import {Layout} from '@components/layouts/layout'
import {
    AdjustmentsHorizontalIcon,
    BoltIcon,
    ChartBarIcon,
    GlobeAltIcon,
    MagnifyingGlassIcon,
    ShieldCheckIcon,
    SparklesIcon
} from '@heroicons/react/24/outline'
import Link from "@components/elements/DocumentLink"
import {Space_Grotesk} from 'next/font/google'
import {ElementType, ReactElement} from 'react'
import type {NextPageWithLayout} from './_app'

type Feature = {
    title: string;
    description: string;
    icon: ElementType;
}

type UseCase = {
    title: string;
    audience: string;
    points: string[];
}

type FAQ = {
    question: string;
    answer: string;
}

const siteName = process.env.NEXT_PUBLIC_WEBSITE_NAME ?? 'Plugins Database';
const siteUrl = process.env.NEXT_PUBLIC_WEBSITE_URL ?? 'https://pluginsdb.com';
const spaceGrotesk = Space_Grotesk({
    subsets: ['latin'],
    weight: ['400', '500', '600', '700'],
    display: 'swap',
});

const heroStats = [
    {
        value: '60k+',
        label: 'WordPress plugins tracked',
        detail: 'Full WordPress.org catalog with tags, languages, and compatibility data.',
    },
    {
        value: 'Data-rich',
        label: 'Filterable signals',
        detail: 'Installs, downloads, ratings, update cadence, languages, tags, and compatibility filters.',
    },
    {
        value: 'Research-grade',
        label: 'Built for due diligence',
        detail: 'Data you can use for migrations, audits, buying decisions, and keyword research.',
    },
];

const signalHighlights = [
    {
        title: 'Search with intent',
        items: ['Name, keyword, tag, author', 'Instant sort by installs, downloads, ratings', 'SEO-friendly discovery for every niche'],
    },
    {
        title: 'Filter with confidence',
        items: ['Minimum installs and downloads', 'Update cadence and recency controls', 'Language packs and compatibility filters'],
    },
    {
        title: 'Decide with evidence',
        items: ['Compare traction and maintenance signals', 'Spot abandoned plugins quickly', 'Document choices with shareable links'],
    },
];

const features: Feature[] = [
    {
        title: 'Precision WordPress plugin search',
        description: 'Search by plugin name, keyword, description, author, or tag, then sort by installs, downloads, rating, or last updated to pinpoint the best option.',
        icon: MagnifyingGlassIcon,
    },
    {
        title: 'Advanced filters that surface winners',
        description: 'Slice plugins by minimum active installs, download velocity, total ratings, update recency, and language packs to match your stack and audience.',
        icon: AdjustmentsHorizontalIcon,
    },
    {
        title: 'Market intelligence without spreadsheets',
        description: 'Compare traction signals, maintenance cadence, and adoption to de-risk migrations, security reviews, and plugin acquisitions.',
        icon: ChartBarIcon,
    },
    {
        title: 'Quality and trust signals',
        description: 'Spot abandoned plugins quickly with recency filters, tested WordPress versions, compatibility notes, and contributor metadata in one view.',
        icon: ShieldCheckIcon,
    },
    {
        title: 'Global coverage and localization',
        description: 'Filter by plugin language packs and internationalization tags so your experiences work worldwide from day one.',
        icon: GlobeAltIcon,
    },
    {
        title: 'Built for builders, agencies, and investors',
        description: 'Whether you are selecting a plugin, planning a rebuild, or screening products to buy, you get the same data-rich foundation.',
        icon: BoltIcon,
    },
];

const useCases: UseCase[] = [
    {
        title: 'Product & engineering teams',
        audience: 'Developers, tech leads, and site owners',
        points: [
            'Shortcut plugin selection with filters for stability, compatibility, and support signals.',
            'Quantify risk with update recency, rating depth, and install velocity before installing.',
            'Document plugin choices with evidence across ratings, downloads, and release cadence.',
        ],
    },
    {
        title: 'Agencies & freelancers',
        audience: 'Implementation partners and web teams',
        points: [
            'Build repeatable plugin stacks by sorting by installs, downloads, and language support.',
            'Answer client questions with data on release cadence, tested WordPress versions, and reviews.',
            'Spot safer alternatives to outdated plugins while preserving feature coverage.',
        ],
    },
    {
        title: 'Investors & product buyers',
        audience: 'M&A, growth, and SEO teams',
        points: [
            'Benchmark plugins by traction metrics to spot breakout categories and underserved niches.',
            'Validate product health with maintenance signals and historical ratings.',
            'Map WordPress marketplace demand to prioritize deals or product theses.',
        ],
    },
];

const faqs: FAQ[] = [
    {
        question: 'What makes this different from WordPress.org search?',
        answer: `${siteName} brings structured filters, sortable data, and research-ready context across installs, downloads, ratings, languages, and update cadence so you can make confident plugin decisions without hopping between tabs.`,
    },
    {
        question: 'How often is the data refreshed?',
        answer: 'The database is kept fresh with recurring syncs from the WordPress marketplace so installs, ratings, and release dates reflect the live ecosystem.',
    },
    {
        question: 'Can I vet plugin quality and risk here?',
        answer: 'Yes. Use filters for last updated, rating counts, compatibility, and language coverage to avoid abandoned plugins and prioritize trustworthy options.',
    },
    {
        question: 'Who is this for?',
        answer: 'Builders shipping client sites, in-house teams maintaining mission-critical plugins, product managers scouting opportunities, and investors researching WordPress plugin acquisitions.',
    },
];

// @ts-ignore
const Page: NextPageWithLayout = () => {
    return (
        <>
            <div
                className={`relative overflow-hidden ${spaceGrotesk.className}`}
            >
                <div
                    className="pointer-events-none absolute inset-x-0 top-[-10rem] h-[20rem] bg-gradient-to-br from-sky-200 via-indigo-100 to-white opacity-70 blur-3xl dark:from-indigo-900 dark:via-slate-900 dark:to-slate-900 dark:opacity-80"
                    aria-hidden="true"
                />

                <section
                    className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-indigo-50 px-4 pb-16 pt-16 text-slate-900 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 dark:text-white sm:px-6 lg:px-12 lg:pb-24 lg:pt-20"
                >
                    <div className="absolute left-1/2 top-[-6rem] h-72 w-72 -translate-x-1/2 rounded-full bg-indigo-200 opacity-40 blur-3xl dark:bg-indigo-800"
                         aria-hidden="true"/>
                    <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
                        <div className="space-y-8">
                            <div className="inline-flex items-center space-x-3 rounded-full bg-white/70 px-4 py-2 text-sm font-semibold uppercase tracking-wide text-indigo-700 shadow-sm ring-1 ring-indigo-200 backdrop-blur dark:bg-white/10 dark:text-indigo-100 dark:ring-indigo-800">
                                <SparklesIcon className="h-5 w-5"/>
                                <span>WordPress plugin intelligence without the noise</span>
                            </div>
                            <div className="space-y-5">
                                <h1 className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
                                    Search every WordPress plugin with market-grade data
                                </h1>
                                <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-200">
                                    {siteName} is a keyword-rich WordPress plugin search engine, analytics layer,
                                    and marketplace research hub. Compare installs, downloads, ratings, update cadence,
                                    tags, and languages in seconds to pick reliable plugins for SEO, security,
                                    eCommerce, memberships, publishing, or acquisitions.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-4">
                                <Link
                                    href="/plugins"
                                    className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-indigo-300 transition hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:shadow-indigo-800/40 dark:hover:shadow-indigo-600/60"
                                >
                                    Launch the Plugin Finder
                                </Link>
                                <Link
                                    href="/about"
                                    className="inline-flex items-center justify-center rounded-xl border border-indigo-200 bg-white/80 px-6 py-3 text-base font-semibold text-indigo-800 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-300 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-indigo-700 dark:bg-white/10 dark:text-indigo-100 dark:hover:border-indigo-500"
                                >
                                    Why teams use {siteName}
                                </Link>
                            </div>
                            <div className="grid gap-4 sm:grid-cols-3">
                                {heroStats.map((stat) => (
                                    <div
                                        key={stat.label}
                                        className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/70"
                                    >
                                        <p className="text-2xl font-semibold text-indigo-700 dark:text-indigo-300">{stat.value}</p>
                                        <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{stat.label}</p>
                                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{stat.detail}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative">
                            <div
                                className="absolute -left-12 -top-10 h-40 w-40 rounded-full bg-indigo-200 blur-3xl dark:bg-indigo-700"
                                aria-hidden="true"
                            />
                            <div
                                className="absolute -right-16 bottom-10 h-48 w-48 rounded-full bg-sky-200 blur-3xl dark:bg-sky-800"
                                aria-hidden="true"
                            />
                            <div
                                className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white/80 shadow-2xl shadow-indigo-100 backdrop-blur dark:border-slate-700 dark:bg-slate-900/80 dark:shadow-indigo-900/50"
                            >
                                <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-700 dark:bg-slate-800/70">
                                    <div className="flex items-center space-x-2 text-sm font-semibold text-slate-600 dark:text-slate-200">
                                        <span className="h-2 w-2 rounded-full bg-green-500"/>
                                        <span>Live marketplace signals</span>
                                    </div>
                                    <div className="flex space-x-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                                        <span className="h-2 w-2 rounded-full bg-red-400"/>
                                        <span className="h-2 w-2 rounded-full bg-yellow-400"/>
                                        <span className="h-2 w-2 rounded-full bg-green-400"/>
                                    </div>
                                </div>
                                <div className="grid gap-4 p-6">
                                    {signalHighlights.map((section) => (
                                        <div
                                            key={section.title}
                                            className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/70"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                                    {section.title}
                                                </div>
                                                <div className="flex items-center space-x-2 text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                                                    <SparklesIcon className="h-4 w-4"/>
                                                    <span>Signals</span>
                                                </div>
                                            </div>
                                            <ul className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                                                {section.items.map((item) => (
                                                    <li key={item} className="flex items-start space-x-2">
                                                        <span className="mt-1 h-2.5 w-2.5 rounded-full bg-indigo-500 dark:bg-indigo-300"/>
                                                        <span>{item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white px-4 py-16 text-slate-900 dark:bg-gray-900 dark:text-gray-100 sm:px-6 lg:px-12">
                    <div className="mx-auto max-w-6xl space-y-6">
                        <div className="space-y-3">
                            <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                                Why teams choose {siteName}
                            </p>
                            <h2 className="text-3xl font-bold sm:text-4xl">Plugin discovery with depth, not guesswork</h2>
                            <p className="max-w-4xl text-lg text-slate-700 dark:text-slate-200">
                                Find, evaluate, and justify every plugin choice with structured marketplace data. {siteName} was built for people who need reliable, keyword-rich insights on WordPress plugins — not just a search box.
                            </p>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                            {features.map((feature) => (
                                <div
                                    key={feature.title}
                                    className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-6 shadow-sm transition hover:-translate-y-1 hover:border-indigo-200 hover:shadow-xl dark:border-slate-800 dark:from-slate-800 dark:to-slate-900 dark:hover:border-indigo-700"
                                >
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 transition group-hover:scale-105 dark:bg-indigo-900/40 dark:text-indigo-200 dark:ring-indigo-800">
                                        <feature.icon className="h-6 w-6" aria-hidden="true"/>
                                    </div>
                                    <h3 className="mt-4 text-xl font-semibold">{feature.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
                                        {feature.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section
                    className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-indigo-100 px-4 py-16 text-slate-900 dark:from-indigo-950 dark:via-slate-900 dark:to-indigo-900 dark:text-white sm:px-6 lg:px-12"
                >
                    <div
                        className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_#c7d2fe_0,_transparent_30%)] opacity-80 dark:bg-[radial-gradient(circle_at_top_left,_#312e81_0,_transparent_35%)] dark:opacity-60"
                        aria-hidden="true"
                    />
                    <div className="relative mx-auto max-w-6xl space-y-10">
                        <div className="space-y-3">
                            <p className="text-sm font-semibold uppercase tracking-wide text-indigo-800 dark:text-indigo-200">
                                Built for the way you work
                            </p>
                            <h2 className="text-3xl font-bold sm:text-4xl text-slate-900 dark:text-white">Use-cases that ship revenue and reliability</h2>
                            <p className="max-w-4xl text-lg text-slate-900 dark:text-indigo-100/90">
                                Whether you are choosing a mission-critical plugin or assessing a product to buy, the WordPress plugin database inside {siteName} keeps everyone aligned on the same source of truth.
                            </p>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {useCases.map((item) => (
                                <div
                                    key={item.title}
                                    className="flex h-full flex-col space-y-4 rounded-3xl border border-indigo-200 bg-white p-6 shadow-xl shadow-indigo-200/80 backdrop-blur-sm ring-1 ring-indigo-200/80 transition dark:border-indigo-800/60 dark:bg-white/5 dark:shadow-lg dark:shadow-indigo-900/40 dark:ring-0"
                                >
                                    <div className="text-sm font-semibold uppercase tracking-wide text-indigo-900 dark:text-indigo-200">{item.audience}</div>
                                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{item.title}</h3>
                                    <ul className="space-y-2 text-sm text-slate-900 dark:text-indigo-50">
                                        {item.points.map((point) => (
                                            <li key={point} className="flex items-start space-x-2">
                                                <span className="mt-1 h-2 w-2 rounded-full bg-indigo-700 dark:bg-indigo-300"/>
                                                <span>{point}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bg-white px-4 py-16 text-slate-900 dark:bg-gray-900 dark:text-gray-100 sm:px-6 lg:px-12">
                    <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
                        <div className="space-y-4">
                            <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                                Data coverage
                            </p>
                            <h2 className="text-3xl font-bold sm:text-4xl">Every decision needs complete plugin context</h2>
                            <p className="text-lg text-slate-700 dark:text-slate-200">
                                Search the entire WordPress marketplace and benchmark plugins with data-rich profiles that include installs, downloads, ratings, changelog cadence, tested WordPress versions, PHP requirements, tags, languages, contributors, and more.
                            </p>
                            <div className="grid gap-3 sm:grid-cols-2">
                                {['Installs, downloads, and velocity signals', 'Ratings, reviews volume, and quality', 'Update recency and maintenance cadence', 'Tags, categories, and niche keywords', 'Language packs and localization support', 'Compatibility with WordPress and PHP'].map((item) => (
                                    <div
                                        key={item}
                                        className="flex items-start space-x-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-100"
                                    >
                                        <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-indigo-600 dark:text-indigo-300" aria-hidden="true"/>
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-4 text-sm text-indigo-900 shadow-sm dark:border-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-100">
                                Keyword-rich insight: use the plugin database as a WordPress plugin search engine, plugin analytics dashboard, WordPress plugin market research lab, and due-diligence checklist in one place.
                            </div>
                        </div>
                        <div className="space-y-6 rounded-3xl border border-slate-200 bg-slate-50 p-8 shadow-sm dark:border-slate-800 dark:bg-slate-800/60">
                            <h3 className="text-2xl font-semibold">Frequently asked questions</h3>
                            <div className="space-y-4">
                                {faqs.map((item) => (
                                    <div key={item.question} className="space-y-2 rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/70">
                                        <div className="flex items-center space-x-2 text-base font-semibold text-slate-900 dark:text-slate-100">
                                            <SparklesIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-300" aria-hidden="true"/>
                                            <span>{item.question}</span>
                                        </div>
                                        <p className="text-sm text-slate-700 dark:text-slate-200">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section
                    className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-indigo-700 to-sky-600 px-4 py-14 text-white sm:px-6 lg:px-12"
                >
                    <div className="absolute inset-0 opacity-40">
                        <div className="absolute -left-10 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" aria-hidden="true"/>
                        <div className="absolute bottom-0 right-0 h-56 w-56 rounded-full bg-white/20 blur-3xl" aria-hidden="true"/>
                    </div>
                    <div className="relative mx-auto flex max-w-5xl flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
                        <div className="space-y-3">
                            <p className="text-sm font-semibold uppercase tracking-wide text-indigo-100">Ready to explore?</p>
                            <h2 className="text-3xl font-bold sm:text-4xl">Find the right WordPress plugin with confidence</h2>
                            <p className="max-w-3xl text-lg text-indigo-100">
                                Launch the Plugin Finder to search, filter, and compare the entire WordPress plugin marketplace. Ship faster, avoid risky installs, and discover the plugins that make your product, client sites, or acquisition targets stronger.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-4">
                            <Link
                                href="/plugins"
                                className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-3 text-base font-semibold text-indigo-700 shadow-lg shadow-indigo-900/40 transition hover:-translate-y-0.5 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-700"
                            >
                                Open the WordPress plugin database
                            </Link>
                            <Link
                                href="/plugins"
                                className="inline-flex items-center justify-center rounded-xl border border-white/60 px-6 py-3 text-base font-semibold text-white transition hover:-translate-y-0.5 hover:border-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-700"
                            >
                                Browse plugins now
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    )
}

Page.getLayout = function getLayout(page: ReactElement) {
    return (
        <Layout
            title={"WordPress Plugin Database & Marketplace Research"}
            description={"Search, filter, and analyze WordPress plugins with installs, downloads, ratings, update cadence, tags, and languages to pick the best plugin or acquisition target."}
            url={`${siteUrl}/`}
            image={`${siteUrl}/images/pluginsdb.jpeg`}
        >
            {page}
        </Layout>
    )
}

export default Page
